import { Editor, TLGroupShape, useEditor as useBootstrapEditor, useMenuClipboardEvents } from '@tldraw/tldraw'
import { Action, ActionId } from '../Types'
import { alignShapes, getCurrentFrame } from '../helpers'
import { useContext } from 'react'
import { VisualEditorContext } from 'components/VisualStudioPlayground/VisualEditorContext'
import { isEmpty } from 'lodash'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useTextActions } from './useTextActions'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { MaskShape } from 'components/VisualStudioPlayground/Shapes/MaskShape/Types'
import { GeoShape } from 'components/VisualStudioPlayground/Shapes/GeoShape/Types'
import { TextBoxShape } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/Types'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { VideoShape } from 'components/VisualStudioPlayground/Shapes/VideoShape/Types'
import { copyTextShapes, prepareTextShapesForPaste } from 'components/VisualStudioPlayground/VisualEditor/utils/copyTextShapes'

/**
 * A centralised set of common actions that can be triggered in different ways inside the visual editor.
 */
export const useActions = (defaultEditor?: Editor) => {
  const bootstrapEditor = useBootstrapEditor()
  const contextEditor = useContext(VisualEditorContext)
  const editor = defaultEditor || (isEmpty(bootstrapEditor) ? contextEditor : bootstrapEditor)
  const { copy, paste } = useMenuClipboardEvents(editor)
  const textActions = useTextActions(editor)

  if (!editor) return {} as Record<ActionId, Action>

  const hasSelectedShapes = () => editor.getSelectedShapes().length > 0

  const hasVideoShapes = () => editor.getSelectedShapes().some((shape) => shape.type === 'video')

  const mustGoBackToSelectToolFirst = () => {
    if (!editor.isIn('select')) {
      editor.complete()
      editor.setCurrentTool('select')
      return false
    }

    return false
  }

  const actions: Array<Action> = [
    {
      id: 'align-left',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        alignShapes(editor, editor.getSelectedShapeIds(), 'left')
      },
    },
    {
      id: 'align-center-horizontal',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        alignShapes(editor, editor.getSelectedShapeIds(), 'center-horizontal')
      },
    },
    {
      id: 'align-right',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        alignShapes(editor, editor.getSelectedShapeIds(), 'right')
      },
    },
    {
      id: 'align-center-vertical',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        alignShapes(editor, editor.getSelectedShapeIds(), 'center-vertical')
      },
    },
    {
      id: 'align-top',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        alignShapes(editor, editor.getSelectedShapeIds(), 'top')
      },
    },
    {
      id: 'align-bottom',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        alignShapes(editor, editor.getSelectedShapeIds(), 'bottom')
      },
    },
    {
      id: 'bring-to-front',
      kbd: 'alt+]',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        editor.bringToFront(editor.getSelectedShapeIds())
      },
    },
    {
      id: 'bring-forward',
      kbd: ']',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        editor.bringForward(editor.getSelectedShapeIds())
      },
    },
    {
      id: 'send-backward',
      kbd: '[',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        editor.sendBackward(editor.getSelectedShapeIds())
      },
    },
    {
      id: 'send-to-back',
      kbd: 'alt+[',
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        editor.sendToBack(editor.getSelectedShapeIds())
      },
    },
    {
      id: 'select-all',
      kbd: ['ctrl+a', 'command+a'],
      onSelect() {
        editor.batch(() => {
          if (mustGoBackToSelectToolFirst()) return

          const nonFrameShapes = editor.getCurrentPageShapes().filter((shape) => shape.type !== 'frame')
          editor.select(...nonFrameShapes)
        })
      },
    },
    {
      id: 'delete',
      kbd: ['del', 'backspace'],
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        const nonFrameShapes = editor.getSelectedShapes().filter((shape) => shape.type !== 'frame')
        editor.deleteShapes(nonFrameShapes)
      },
    },
    {
      id: 'undo',
      kbd: ['ctrl+z', 'command+z'],
      onSelect() {
        const editingShape = editor.getEditingShape()

        if (editingShape) {
          editor.setCurrentTool('select.idle')
        }

        editor.undo()
      },
    },
    {
      id: 'redo',
      kbd: ['ctrl+shift+z', 'command+shift+z'],
      onSelect() {
        editor.redo()
      },
    },
    {
      id: 'duplicate',
      kbd: ['ctrl+d', 'command+d'],
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return
        if (hasVideoShapes()) return

        const nonFrameShapes = editor.getSelectedShapes().filter((shape) => shape.type !== 'frame')
        editor.duplicateShapes(nonFrameShapes, { x: 20, y: 20 })
      },
    },
    {
      id: 'ungroup',
      kbd: ['ctrl+shift+g', 'command+shift+g'],
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        editor.mark('ungroup')
        editor.ungroupShapes(editor.getSelectedShapeIds())
      },
    },
    {
      id: 'group',
      kbd: ['ctrl+g', 'command+g'],
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        const onlySelectedShape = editor.getOnlySelectedShape()
        if (onlySelectedShape && editor.isShapeOfType<TLGroupShape>(onlySelectedShape, 'group')) {
          editor.mark('ungroup')
          editor.ungroupShapes(editor.getSelectedShapeIds())
        } else {
          editor.mark('group')
          editor.groupShapes(editor.getSelectedShapeIds())
        }
      },
    },
    {
      id: 'copy',
      kbd: ['ctrl+c', 'command+c'],
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return
        if (hasVideoShapes()) return

        const selectedShapes = editor.getSelectedShapes()

        if (selectedShapes.some((shape) => shape.type === 'frame')) {
          return
        }

        if (selectedShapes.every((shape) => shape.type === 'textBox')) {
          copyTextShapes(editor).catch((error) => {
            reportError(error)
            showErrorMessageToast('Unable to copy text. Please try again.')
          })
          return
        }

        copy('unknown')
      },
    },
    {
      id: 'paste',
      kbd: ['ctrl+v', 'command+v'],
      onSelect() {
        navigator.clipboard
          ?.read()
          .then(prepareTextShapesForPaste)
          .then((clipboardItems) => {
            paste(clipboardItems, 'unknown', editor.inputs.currentPagePoint).then(() => {
              setTimeout(() => {
                const currentFrame = getCurrentFrame(editor)
                if (!currentFrame) {
                  reportError('No frame found to reparent to')
                  showErrorMessageToast('No frame found to reparent to')
                  return
                }
                const selectedShapes = editor.getSelectedShapes()
                editor.reparentShapes(selectedShapes, currentFrame.id)
                editor.getCurrentPageShapes().forEach((shape) => {
                  if (shape.type !== 'frame' && shape.parentId === 'page:page') {
                    editor.deleteShape(shape)
                  }
                })
              }, 200)
            })
          })
      },
    },
    {
      id: 'reparent',
      kbd: ['ctrl+shift+p', 'command+shift+p'],
      onSelect() {
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return

        const frame = getCurrentFrame(editor)

        if (!frame) {
          showErrorMessageToast('No frame found to reparent to')
          return
        }

        editor.mark('reparent')
        editor.reparentShapes(editor.getSelectedShapeIds(), frame.id)
      },
    },
    {
      id: 'toggle-lock',
      onSelect() {
        const selectedShape = editor.getOnlySelectedShape() as
          | ImageShape
          | MaskShape
          | GeoShape
          | TextBoxShape
          | FrameShape
          | VideoShape
        if (!selectedShape) {
          return
        }

        editor.updateShape({
          ...selectedShape,
          props: {
            ...selectedShape.props,
            isEditable: !selectedShape.props.isEditable,
          },
        })
      },
    },
    ...textActions,
  ]

  return Object.fromEntries(actions.map((action) => [action.id, action])) as Record<ActionId, Action>
}
